import * as CardCrawl from "@ravalmatic/cardcrawl-engine";
import { GameConstants } from "../../GameConstants";
import { GameVars } from "../../GameVars";
import { LowerTableauContainer } from "./tableaus/LowerTableauContainer";
import { UpperTableauContainer } from "./tableaus/UpperTableauContainer";
import { Chest } from "./tableaus/Chest";
import { Card } from "./cards/Card";
import { Deck } from "./cards/Deck";
import { FX } from "./cards/FX/FX";
import { Utils } from "../../utils/Utils";
import { EventType } from "../../Types";
import { BoardScene } from "./BoardScene";

export class BoardContainer extends Phaser.GameObjects.Container {

    public static readonly CARD_WIDTH: number = 172;
    public static readonly CARD_HEIGHT: number = 230;
    public static readonly UPPER_TABLEAU_CONTAINER_Y = -190;
    public static readonly LOWER_TABLEAU_CONTAINER_Y = 75;

    public static currentInstance: BoardContainer;

    public cardSelectionBlocked: boolean;
    public upperTableauContainer: UpperTableauContainer;
    public lowerTableauContainer: LowerTableauContainer;
    public chest: Chest;
    public deck: Deck;

    private timerEvent: Phaser.Time.TimerEvent;

    constructor(scene: Phaser.Scene) {
        
        super(scene);

        BoardContainer.currentInstance = this;

        this.x = GameConstants.GAME_WIDTH / 2;
        this.y = GameConstants.GAME_HEIGHT / 2;
        this.scaleY = GameVars.scaleY;

        this.cardSelectionBlocked = false;
        this.timerEvent = null;

        this.deck = new Deck(scene);
        this.deck.y = -310;
        this.add(this.deck);

        this.chest = new Chest(scene);
        this.add(this.chest);

        this.upperTableauContainer = new UpperTableauContainer(scene);
        this.upperTableauContainer.y = BoardContainer.UPPER_TABLEAU_CONTAINER_Y;
        this.add(this.upperTableauContainer);

        this.lowerTableauContainer = new LowerTableauContainer(scene);
        this.lowerTableauContainer.y = BoardContainer.LOWER_TABLEAU_CONTAINER_Y;
        this.add(this.lowerTableauContainer);
    }

    public startGame(): void {
       
        this.deck.startGame();
        this.chest.startGame();
    }

    public startTutorial(): void {

        this.deck.startTutorial();
    }

    public onGameOver(): void {

        this.deck.onGameOver();
        this.lowerTableauContainer.onGameOver();
        this.chest.onGameOver();

        let delay: number;

        if (GameVars.victory) {
            delay = BoardScene.DELAY_GAME_OVER;
        } else if (GameVars.cardCrawlEngine.getState().deck.length === 0) {
            delay = 2500;
        } else {
            delay = 0;
        }

        this.scene.tweens.add({
            targets: this.deck,
            alpha: 0,
            ease: Phaser.Math.Easing.Linear,
            duration: 400,
            delay: delay
        });

        this.scene.tweens.add({
            targets: this.upperTableauContainer,
            alpha: 0,
            ease: Phaser.Math.Easing.Linear,
            duration: 400
        });
    }

    public onActionProcessed(): void {

        this.upperTableauContainer.onActionProcessed();
        this.lowerTableauContainer.onActionProcessed();
        this.chest.onActionProcessed();

        // ver si cartas han vuelto a la baraja
        const state = GameVars.cardCrawlEngine.getState();
       
        if (state.uiData.cardsReturnedToDeck > 0) {
            this.deck.addCards(state.uiData.cardsReturnedToDeck);
        }

        // si se han repartido cartas bloqueamos las cartas que hay sobre los tableros
        if (this.timerEvent) {
            this.scene.time.removeEvent(this.timerEvent);
        }

        if (state.uiData.cardsDealt !== null) {

            this.cardSelectionBlocked = true;
            const delay = state.uiData.cardsDealt === 4 ? 300 * state.uiData.cardsDealt : 800 * state.uiData.cardsDealt;
            this.timerEvent = this.scene.time.delayedCall(delay, () => {
                this.cardSelectionBlocked = false;
            }, [], this);
        }
    }

    public showAllowedMoves(card: CardCrawl.CardData): void {

        this.upperTableauContainer.showAllowedMoves(card);
        this.lowerTableauContainer.showAllowedMoves(card);
        this.chest.showAllowedMoves(card);
    }

    public hideAllowedMoves(): void {
        
        this.upperTableauContainer.hideAllowedMoves();
        this.lowerTableauContainer.hideAllowedMoves();
        this.chest.hideAllowedMoves();
    }

    public onCardDropped(card: Card): void {

        let isMoveValid = false;

        this.hideAllowedMoves();

        GameVars.droppedCardPosition = {x: card.x, y: card.y};

        let x = card.x;
        let y = card.y - this.upperTableauContainer.y;

        let index = this.upperTableauContainer.getOverlappingIndex(x, y);

        if (index !== null) {

            isMoveValid = Utils.isMoveValid(CardCrawl.TableauType.UPPER, index);

            if (isMoveValid) {

                card.y -= this.upperTableauContainer.y;
                card.tweening = true;
                this.bringToTop(this.upperTableauContainer);
                this.upperTableauContainer.add(card);

                const p = this.upperTableauContainer.getCardPosition(index);

                FX.moveTo(card, p, 150, 0, () => {
                    card.tweening = false;
                    GameVars.eventsDispatcher.dispatchEvent(new CardCrawl.Event(EventType.CARD_DROPPED, [card.id, CardCrawl.TableauType.UPPER, index]));

                }, this);

                FX.audioFXCardDropped(card, CardCrawl.TableauType.UPPER, index);

                return;
            }
        }

        y = card.y - this.lowerTableauContainer.y;

        index = this.lowerTableauContainer.getOverlappingIndex(x, y);

        if (index !== null) {

            isMoveValid = Utils.isMoveValid(CardCrawl.TableauType.LOWER, index);

            if (isMoveValid) {

                card.y -= this.lowerTableauContainer.y;
                card.tweening = true;
                this.bringToTop(this.lowerTableauContainer);
                this.lowerTableauContainer.add(card);

                // SACAR LAS COORDENADAS Y EL ANGULO DE LA POSICIPON DEL TABLERO
                const p = this.lowerTableauContainer.getCardPosition(index);

                FX.moveTo(card, p, 150, 0, () => {
                    card.tweening = false;
                    GameVars.eventsDispatcher.dispatchEvent(new CardCrawl.Event(EventType.CARD_DROPPED, [card.id, CardCrawl.TableauType.LOWER, index]));

                }, this);

                FX.audioFXCardDropped(card, CardCrawl.TableauType.LOWER, index);
                
                return;
            }
        }

        x = card.x - this.chest.x;
        y = card.y - this.chest.y;

        if (this.chest.positionRect.contains(x, y)) {

            isMoveValid = Utils.isMoveValid(CardCrawl.TableauType.SHOP, index);

            if (isMoveValid) {

                card.tweening = true;

                FX.moveTo(card, {x: this.chest.x - 50, y: this.chest.y - 50, angle: 0}, 350, 0, () => {
                    card.tweening = false;
                    GameVars.eventsDispatcher.dispatchEvent(new CardCrawl.Event(EventType.CARD_DROPPED, [card.id, CardCrawl.TableauType.SHOP, index]));

                }, this);

                FX.audioFXCardDropped(card, CardCrawl.TableauType.SHOP);

                return;
            }
        }

        card.moveBackToTableau();
    }

    public getCardContainerById(id: number): Card {

        let cardContainer: Card;

        cardContainer = this.upperTableauContainer.cards.find((cardContainer: Card) => {
            return cardContainer && cardContainer.id === id;
        });

        if (!cardContainer) {

            cardContainer = this.lowerTableauContainer.cards.find((cardContainer: Card) => {
                return cardContainer && cardContainer.id === id;
            });
        }

        return cardContainer;
    }

    public isCardBeingDragged(): boolean {

        return this.upperTableauContainer.cards.some((card: Card) => card.dragging) || this.lowerTableauContainer.cards.some((card: Card) => card.dragging);
    }
}
